package fyi.moko.hotpl.data.network

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.storage.Data
import dev.gitlive.firebase.storage.FirebaseStorageException
import dev.gitlive.firebase.storage.storage
import dev.gitlive.firebase.storage.storageMetadata
import fyi.moko.hotpl.domain.service.PhotoBlobStore
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.statement.readRawBytes
import io.ktor.http.HttpStatusCode

/** Port the Storage emulator listens on (`firebase.json` → emulators.storage.port). */
private const val STORAGE_EMULATOR_PORT = 9199

/** Bridges a Kotlin [ByteArray] to GitLive's per-platform upload payload. */
internal expect fun ByteArray.toStorageData(): Data

/**
 * [PhotoBlobStore] on Cloud Storage for Firebase: `users/{uid}/photos/{fileName}`, guarded by
 * `storage.rules` (owner only, image content types, 5MB ceiling).
 *
 * Downloads go through a signed download URL fetched from the SDK and then plain HTTP via
 * [http]. GitLive 2.4.0 exposes no `getBytes`, and the Ktor client already exists for the
 * Places API, so this keeps one download path for all three platforms instead of three
 * native ones.
 */
class FirebasePhotoBlobStore(
    emulatorHost: String? = null,
    private val http: HttpClient,
) : PhotoBlobStore {
    private val storage = Firebase.storage

    init {
        if (emulatorHost != null) {
            try {
                storage.useEmulator(emulatorHost, STORAGE_EMULATOR_PORT)
            } catch (e: Exception) {
                // Ignore re-configuration errors, same as the Firestore data source.
            }
        }
    }

    private fun ref(uid: String, fileName: String) = storage.reference(objectPath(uid, fileName))
    private fun sharedRef(uid: String, fileName: String) = storage.reference(sharedObjectPath(uid, fileName))

    override suspend fun upload(uid: String, fileName: String, bytes: ByteArray) {
        // Explicit content type: the rules reject anything without an image content type, and the SDKs
        // don't sniff bytes.
        ref(uid, fileName).putData(bytes.toStorageData(), storageMetadata { contentType = "image/jpeg" })
    }

    override suspend fun download(uid: String, fileName: String): ByteArray? =
        downloadFrom(ref(uid, fileName))

    override suspend fun uploadShared(uid: String, fileName: String, bytes: ByteArray) {
        sharedRef(uid, fileName).putData(bytes.toStorageData(), storageMetadata { contentType = "image/jpeg" })
    }

    override suspend fun downloadShared(ownerUid: String, fileName: String): ByteArray? =
        downloadFrom(sharedRef(ownerUid, fileName))

    override suspend fun deleteShared(uid: String, fileName: String) {
        try {
            sharedRef(uid, fileName).delete()
        } catch (e: FirebaseStorageException) {
            // Already gone — same reasoning as delete().
        }
    }

    private suspend fun downloadFrom(reference: dev.gitlive.firebase.storage.StorageReference): ByteArray? {
        val url = try {
            reference.getDownloadUrl()
        } catch (e: FirebaseStorageException) {
            // Object-not-found surfaces here, as does a permission denial for a photo that
            // belongs to a previous account. Both read as "nothing to fetch".
            return null
        }
        val response = http.get(url)
        if (response.status == HttpStatusCode.NotFound) return null
        if (!response.status.isSuccess()) {
            throw PhotoDownloadException(response.status.value)
        }
        return response.readRawBytes()
    }

    override suspend fun delete(uid: String, fileName: String) {
        try {
            ref(uid, fileName).delete()
        } catch (e: FirebaseStorageException) {
            // Already gone (never uploaded, or deleted from another device) — that's the goal.
        }
    }

    companion object {
        fun objectPath(uid: String, fileName: String): String = "users/$uid/photos/$fileName"
        fun sharedObjectPath(uid: String, fileName: String): String = "shared/$uid/$fileName"
    }
}

private fun HttpStatusCode.isSuccess(): Boolean = value in 200..299

/** A download URL resolved but the HTTP fetch was refused; [status] is the only detail worth logging. */
class PhotoDownloadException(val status: Int) : Exception("photo download failed: HTTP $status")
